import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { UserActions } from "../user/UserActions";
import { HttpError } from "./Types";

const SUPERVISOR_REQUIRED_CODES = new Set([402, 409]);

const AUDIT_ORIGIN_HEADER = "X-Nano-Origin";
const AI_ASSISTED_ORIGIN = "ai-assisted";
// A form submit fires its write within the same tick or a few ms later; a bounded window
// keeps a submit that never produced a write from tagging an unrelated save much later.
const AI_ASSISTED_ARM_WINDOW_MS = 2000;
const WRITE_METHODS = new Set(["post", "put", "delete"]);

/** One Server-Sent Events frame: its event name and its data lines joined by newlines. */
export interface ServerSentEvent {
  name: string;
  data: string;
}

export class HttpService {
  axios: AxiosInstance;
  lastActivity: number = Date.now();
  private aiAssistedArmedUntil = 0;
  constructor(private baseUrl: string) {
    this.axios = axios.create({
      headers: {
        "Content-type": "application/json",
      },
    });
    this.axios.interceptors?.request?.use((cfg) => {
      if (cfg.url?.indexOf("/user/ping") == -1) {
        this.updateActivity();
      } else {
        console.warn("Will ignore ping for activity check");
      }
      return cfg;
    });
    this.axios.interceptors?.request?.use((config) => this.tagAiAssistedWrite(config));
  }
  updateActivity() {
    this.lastActivity = Date.now();
  }

  /**
   * Marks the next write (POST / PUT / DELETE, chat traffic excluded) as a save of
   * AI-applied values so the backend audits it with origin AI_ASSISTED. The admin's submit
   * handler runs before its request is dispatched, which is why the caller arms here and
   * the interceptor tags; it is advisory — a submit that fires several writes tags only
   * the first.
   */
  armAiAssistedOrigin(): void {
    this.aiAssistedArmedUntil = Date.now() + AI_ASSISTED_ARM_WINDOW_MS;
  }

  /** Withdraws the arm before the window runs out — for a submit that turned out to fire no write. */
  disarmAiAssistedOrigin(): void {
    this.aiAssistedArmedUntil = 0;
  }

  private tagAiAssistedWrite(config: AxiosRequestConfig): AxiosRequestConfig {
    if (this.aiAssistedArmedUntil === 0) {
      return config;
    }
    if (Date.now() > this.aiAssistedArmedUntil) {
      this.aiAssistedArmedUntil = 0;
      return config;
    }
    if (!WRITE_METHODS.has(config.method ?? "") || config.url?.includes("/admin-mcp/")) {
      return config;
    }
    config.headers = { ...config.headers, [AUDIT_ORIGIN_HEADER]: AI_ASSISTED_ORIGIN };
    this.aiAssistedArmedUntil = 0;
    return config;
  }
  hasSeenActivitySince(minutes: number) {
    return this.lastActivity > Date.now() - minutes * 60 * 1000;
  }

  getBaseUrl = () => this.baseUrl;

  get<R>(url: string): Promise<R> {
    return new Promise((resolve, reject) => {
      this.axios
        .get(this.baseUrl + url, { withCredentials: true })
        .then((response) => resolve(response.data))
        .catch((e) => this.handleError(url, e, reject));
    });
  }
  post<R, E>(url: string, entity: E, config?: AxiosRequestConfig): Promise<R> {
    return new Promise((resolve, reject) => {
      this.axios
        .post(this.baseUrl + url, entity, { ...config, withCredentials: true })
        .then((response) => resolve(response.data))
        .catch((e) => this.handleError(url, e, reject));
    });
  }

  put<R, E>(url: string, entity: E): Promise<R> {
    return new Promise((resolve, reject) => {
      this.axios
        .put(this.baseUrl + url, entity, { withCredentials: true })
        .then((response) => resolve(response.data))
        .catch((e) => this.handleError(url, e, reject));
    });
  }

  delete(url: string): Promise<{}> {
    return new Promise((resolve, reject) => {
      this.axios
        .delete(this.baseUrl + url, { withCredentials: true })
        .then((r) => resolve(r.data))
        .catch((e) => this.handleError(url, e, reject));
    });
  }
  deleteWithBody<R, E>(url: string, entity: E): Promise<R> {
    return new Promise((resolve, reject) => {
      this.axios
        .request({
          method: "DELETE",
          url: this.baseUrl + url,
          data: entity,
          withCredentials: true,
        })
        .then((r) => resolve(r.data))
        .catch((e) => this.handleError(url, e, reject));
    });
  }

  /**
   * POSTs a JSON body and reads the Server-Sent Events answer, calling onEvent once per event until
   * the stream ends. A non-2xx answer rejects with the same HttpError the axios methods produce,
   * side effects included; a transport failure or an abort rejects with fetch's own error.
   */
  async postEventStream<E>(url: string, entity: E, onEvent: (event: ServerSentEvent) => void, signal?: AbortSignal): Promise<void> {
    // fetch bypasses the axios interceptors: activity is recorded here, and the AI-assisted arm is
    // deliberately neither consumed nor sent.
    this.updateActivity();
    const response = await fetch(this.baseUrl + url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(entity),
      signal,
    });
    if (!response.ok) {
      const contentType = response.headers.get("content-type") ?? undefined;
      throw this.rejectionFor(response.status, contentType, await readErrorBody(response, contentType));
    }
    if (!response.body) {
      return;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let chunk = await reader.read();
    while (!chunk.done) {
      buffer = (buffer + decoder.decode(chunk.value, { stream: true })).replace(/\r\n/g, "\n");
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const event = parseEventFrame(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        if (event) {
          onEvent(event);
        }
        boundary = buffer.indexOf("\n\n");
      }
      chunk = await reader.read();
    }
  }

  handleError(path: string, response: any, reject: (msg?: HttpError) => void) {
    if (path.indexOf("user/login") != -1 || path.indexOf("user/info") != -1) {
      reject({ message: "Not logged in", status: response.status });
      return;
    }
    reject(this.rejectionFor(response.request.status, response.response.headers?.["content-type"], response.response.data));
  }

  /**
   * Turns a failed response into the HttpError callers reject with. A 401 or 403 also refreshes the
   * user info, which logs the admin out when the session is gone, and a 403 flags the request as forbidden.
   */
  rejectionFor(status: number, contentType: string | undefined, data: unknown): HttpError {
    const userActions = new UserActions();
    if (status == 401) {
      userActions.getUserInfo(false);
    } else if (status == 403) {
      userActions.getUserInfo(false);
      userActions.requestForbidden();
    }
    if (status == 401 || status == 403) {
      return { message: "Access denied", status: status };
    }
    if (contentType?.includes("application/json")) {
      const body = data as HttpError["data"];
      const isSupervisorRequired = SUPERVISOR_REQUIRED_CODES.has(status);
      const message = (body?.message as string | undefined) || `Error with status ${status}`;
      return { message, data: body, status: status, isSupervisorRequired };
    }
    if (contentType?.includes("text/plain")) {
      const isSupervisorRequired = SUPERVISOR_REQUIRED_CODES.has(status);
      return { message: data as string, status: status, isSupervisorRequired };
    }
    if (status >= 500 && status < 600) {
      return { message: "Internal server error, if this persist please contact support. ", status: status };
    }
    return { message: "Error with status " + status, status: status };
  }
}

/** Undefined for a frame without data, such as a keep-alive comment. */
export function parseEventFrame(raw: string): ServerSentEvent | undefined {
  let name = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) {
      name = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      // The spec strips exactly one leading space, so data that itself starts with a space survives.
      dataLines.push(line.slice("data:".length).replace(/^ /, ""));
    }
  }
  return dataLines.length ? { name, data: dataLines.join("\n") } : undefined;
}

// Parsed only when the server says JSON, so a malformed body still reaches rejectionFor as text.
async function readErrorBody(response: Response, contentType: string | undefined): Promise<unknown> {
  const text = await response.text();
  if (!contentType?.includes("application/json")) {
    return text;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
